import { Download, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>;
	readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/**
 * Renders an install-to-homescreen prompt card when the browser fires
 * `beforeinstallprompt`. Dismissed state is persisted to sessionStorage so
 * the card doesn't re-appear after a soft navigation within the same tab.
 */
export function InstallCard() {
	const { t } = useTranslation();
	const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (sessionStorage.getItem("pwa-install-dismissed")) return;

		function handler(e: Event) {
			e.preventDefault();
			deferredPrompt.current = e as BeforeInstallPromptEvent;
			setVisible(true);
		}

		window.addEventListener("beforeinstallprompt", handler);
		return () => window.removeEventListener("beforeinstallprompt", handler);
	}, []);

	const handleInstall = async () => {
		if (!deferredPrompt.current) return;
		await deferredPrompt.current.prompt();
		await deferredPrompt.current.userChoice;
		deferredPrompt.current = null;
		setVisible(false);
	};

	const handleDismiss = () => {
		sessionStorage.setItem("pwa-install-dismissed", "1");
		setVisible(false);
	};

	if (!visible) return null;

	return (
		<div className="fixed bottom-20 inset-x-4 md:bottom-6 md:left-auto md:right-6 md:w-80 z-50 bg-base-100 border border-base-300 rounded-2xl shadow-xl p-4 flex items-start gap-3">
			<div className="flex-1 min-w-0">
				<p className="font-semibold text-sm">{t("pwa.installTitle")}</p>
				<p className="text-xs text-base-content/60 mt-0.5">
					{t("pwa.installDescription")}
				</p>
				<button
					type="button"
					onClick={handleInstall}
					className="btn btn-primary btn-sm rounded-full mt-2 gap-1"
				>
					<Download className="h-3.5 w-3.5" />
					{t("pwa.install")}
				</button>
			</div>
			<button
				type="button"
				onClick={handleDismiss}
				aria-label={t("common.dismiss")}
				className="shrink-0 btn btn-ghost btn-xs btn-circle"
			>
				<X className="h-4 w-4" />
			</button>
		</div>
	);
}
